import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useApi } from '../../api/api';
import './AclScenario.css';

// Color palette for different scenarios
const SCENARIO_COLORS = [
  '#3b82f6', // Blue (Baseline)
  '#ef4444', // Red  
  '#10b981', // Green
  '#f59e0b', // Yellow
  '#8b5cf6', // Purple
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#84cc16', // Lime
  '#ec4899', // Pink
  '#6b7280'  // Gray
];

const AclScenarioComponent = ({ 
  aclAnalyses, 
  selectedYear,
  selectedSessionId,
  loading: parentLoading 
}) => {
  const [selectedAclScenarios, setSelectedAclScenarios] = useState([]);
  const [multiScenarioData, setMultiScenarioData] = useState(null);
  const [aclDataLoading, setAclDataLoading] = useState(false);
  const [baselineSession, setBaselineSession] = useState(null);

  // Get the getSessions function from useApi hook directly
  const { getFleetQuotaUtilization, getSessions } = useApi();

  // Load ACL analyses and find baseline session
  useEffect(() => {
    const loadBaselineSession = async () => {
      if (!aclAnalyses || aclAnalyses.length === 0) return;

      // Check if baseline is already in the analyses
      let foundBaseline = null;
      aclAnalyses.forEach(analysis => {
        const sessionsToCheck = analysis.sensitivity_sessions ? analysis.sensitivity_sessions : [analysis];
        sessionsToCheck.forEach(session => {
          if (session.session_name && session.session_name.includes('Baseline')) {
            foundBaseline = {
              session_id: session.session_id,
              session_name: session.session_name,
              description: session.description
            };
          }
        });
      });

      if (foundBaseline) {
        console.log('✓ Baseline found in ACL analyses:', foundBaseline);
        setBaselineSession(foundBaseline);
        return;
      }

      // If baseline not found in ACL analyses, look for it in the sessions
      console.log('Baseline not found in ACL analyses, searching sessions for baseline...');
      try {
        const sessionsResponse = await getSessions();
        
        let sessionsData = [];
        if (sessionsResponse?.data?.success && sessionsResponse?.data?.results) {
          sessionsData = sessionsResponse.data.results;
        } else if (Array.isArray(sessionsResponse?.data)) {
          sessionsData = sessionsResponse.data;
        }

        const baselineSession = sessionsData.find(session => 
          session.session_name && session.session_name.includes('Baseline')
        );

        if (baselineSession) {
          console.log('✓ Found baseline in sessions:', baselineSession);
          setBaselineSession({
            session_id: baselineSession.session_id,
            session_name: baselineSession.session_name,
            description: baselineSession.description
          });
        } else {
          console.log('⚠ No baseline session found');
        }
      } catch (error) {
        console.error('Error fetching sessions to find baseline:', error);
      }
    };

    loadBaselineSession();
  }, [aclAnalyses, getSessions]);

  // Parse session names to extract species and increment values
  const scenarioMatrix = useMemo(() => {
    if (!aclAnalyses || aclAnalyses.length === 0) return { matrix: {}, baseline: baselineSession, species: [], increments: [] };

    console.log('=== PARSING ACL ANALYSES ===');
    console.log('Raw aclAnalyses data:', aclAnalyses);
    console.log('Number of analyses:', aclAnalyses.length);
    console.log('Separate baseline session:', baselineSession);

    const matrix = {};
    let baseline = baselineSession; // Use the separate baseline state
    const increments = ['-30', '-10', '-5', '0', '+5', '+10', '+30']; // Percentage values

    // Initialize matrix structure
    const allSpecies = new Set();

    // Handle flat array structure - each analysis IS a session
    aclAnalyses.forEach((session, index) => {
      console.log(`Processing analysis ${index}:`, session);
      
      // Handle both nested structure (sensitivity_sessions) and flat structure
      const sessionsToProcess = session.sensitivity_sessions ? session.sensitivity_sessions : [session];
      console.log(`Sessions to process for analysis ${index}:`, sessionsToProcess.length);
      
      sessionsToProcess.forEach((sessionItem, sessionIndex) => {
        const sessionName = sessionItem.session_name;
        const sessionId = sessionItem.session_id;
        console.log(`  Processing session ${sessionIndex}: "${sessionName}" (${sessionId})`);
        
        // Check if this is baseline (but we now handle baseline separately)
        if (sessionName && sessionName.includes('Baseline')) {
          console.log('  ✓ Found baseline in ACL analyses (will be handled separately)');
          return;
        }

        // Parse percentage-based scenarios: "anything - [+/-]X% Species Name Change"
        if (sessionName) {
          const match = sessionName.match(/([+-]?\d+)%\s+(.+?)\s+Change/);
          if (match) {
            const percentChange = match[1]; // e.g., "+30", "-10", "0"
            const species = match[2].trim(); // e.g., "White Hake", "GB Cod"
            console.log(`  ✓ Parsed scenario: ${species} ${percentChange}% (${sessionId})`);
            
            allSpecies.add(species);
            
            if (!matrix[species]) {
              matrix[species] = {};
            }
            
            // Use percentage as the key
            matrix[species][percentChange] = {
              session_id: sessionId,
              session_name: sessionName,
              description: sessionItem.description,
              increment: percentChange,
              species: species
            };
          } else {
            console.log(`  ⚠ Could not parse session name: "${sessionName}"`);
          }
        }
      });
    });

    // Ensure all species have all increment slots (for disabled state)
    const sortedSpecies = Array.from(allSpecies).sort();
    sortedSpecies.forEach(species => {
      if (!matrix[species]) {
        matrix[species] = {};
      }
      increments.forEach(increment => {
        if (!matrix[species][increment]) {
          matrix[species][increment] = null; // Will render as disabled
        }
      });
    });

    console.log('=== PARSING RESULTS ===');
    console.log('Baseline found:', baseline);
    console.log('Species found:', sortedSpecies);
    console.log('Matrix structure:', matrix);

    return { 
      matrix, 
      baseline, 
      species: sortedSpecies,
      increments 
    };
  }, [aclAnalyses, baselineSession]);

  // Get scenario color by index
  const getScenarioColor = useCallback((index) => {
    return SCENARIO_COLORS[index % SCENARIO_COLORS.length];
  }, []);

  // Generate meaningful legend labels
  const getLegendLabels = useMemo(() => {
    const labels = [];
    
    // Always include baseline first
    if (scenarioMatrix.baseline) {
      labels.push({
        sessionId: scenarioMatrix.baseline.session_id,
        label: 'Baseline',
        color: getScenarioColor(0)
      });
      console.log('Added baseline to legend:', scenarioMatrix.baseline.session_id);
    } else {
      console.log('No baseline found for legend');
    }
    
    // Add selected scenarios
    selectedAclScenarios.forEach((sessionId, index) => {
      // Find the scenario details
      for (const species of scenarioMatrix.species || []) {
        for (const increment of scenarioMatrix.increments || []) {
          const scenario = scenarioMatrix.matrix[species]?.[increment];
          if (scenario && scenario.session_id === sessionId) {
            labels.push({
              sessionId,
              label: `${species} ${increment}%`,
              color: getScenarioColor(index + 1) // +1 because baseline takes index 0
            });
            console.log(`Added scenario to legend: ${species} ${increment}% (${sessionId})`);
            return; // Found the scenario, move to next sessionId
          }
        }
      }
    });
    
    console.log('Final legend labels:', labels);
    return labels;
  }, [scenarioMatrix, selectedAclScenarios, getScenarioColor]);

  // Handle scenario toggle
  const handleScenarioToggle = useCallback((sessionId) => {
    setSelectedAclScenarios(prev => {
      if (prev.includes(sessionId)) {
        return prev.filter(id => id !== sessionId);
      } else {
        return [...prev, sessionId];
      }
    });
  }, []);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    const allSessionIds = [];
    scenarioMatrix.species.forEach(species => {
      scenarioMatrix.increments.forEach(increment => {
        const scenario = scenarioMatrix.matrix[species][increment];
        if (scenario) {
          allSessionIds.push(scenario.session_id);
        }
      });
    });
    setSelectedAclScenarios(allSessionIds);
  }, [scenarioMatrix]);

  // Handle clear all
  const handleClearAll = useCallback(() => {
    setSelectedAclScenarios([]);
  }, []);

  // Helper function to combine scenario data
  const combineScenarioData = useCallback((responses, scenarioIds) => {
    if (!responses || responses.length === 0) return null;

    // Always include baseline if available
    const allScenarioIds = scenarioMatrix.baseline 
      ? [scenarioMatrix.baseline.session_id, ...scenarioIds]
      : scenarioIds;

    console.log('=== COMBINING SCENARIO DATA ===');
    console.log('Selected scenario IDs:', scenarioIds);
    console.log('All scenario IDs (with baseline):', allScenarioIds);
    console.log('Baseline info:', scenarioMatrix.baseline);
    console.log('Number of API responses:', responses.length);

    // Get all unique species from all responses
    const allSpecies = new Set();
    const scenarioDataMap = {};

    responses.forEach((response, index) => {
      const sessionId = allScenarioIds[index];
      console.log(`Processing response ${index} for session ${sessionId}:`);
      console.log(`  Response success:`, response?.data?.success);
      console.log(`  Response data:`, response?.data);
      
      if (response?.data?.success && response.data.species_breakdown) {
        scenarioDataMap[sessionId] = {};
        response.data.species_breakdown.forEach(species => {
          allSpecies.add(species.species_name);
          scenarioDataMap[sessionId][species.species_name] = species.utilization_pct || 0;
        });
        console.log(`  ✓ Session ${sessionId} has data for ${response.data.species_breakdown.length} species`);
      } else {
        console.log(`  ⚠ No valid data for session ${sessionId}`);
      }
    });

    console.log('All species found:', Array.from(allSpecies));
    console.log('Scenario data map keys:', Object.keys(scenarioDataMap));

    // Create combined dataset
    const combinedData = Array.from(allSpecies).map(speciesName => {
      const dataPoint = { species_name: speciesName };
      
      allScenarioIds.forEach(sessionId => {
        const utilization = scenarioDataMap[sessionId]?.[speciesName] || 0;
        dataPoint[sessionId] = utilization;
        console.log(`  ${speciesName} - Session ${sessionId}: ${utilization}%`);
      });
      
      return dataPoint;
    });

    // Sort by average utilization across scenarios
    combinedData.sort((a, b) => {
      const avgA = allScenarioIds.reduce((sum, id) => sum + (a[id] || 0), 0) / allScenarioIds.length;
      const avgB = allScenarioIds.reduce((sum, id) => sum + (b[id] || 0), 0) / allScenarioIds.length;
      return avgB - avgA;
    });

    console.log('Final combined data:', combinedData);
    return combinedData;
  }, [scenarioMatrix]);

  // Load Multi-Scenario Quota Data
  const loadMultiScenarioQuotaData = useCallback(async () => {
    if (selectedAclScenarios.length === 0) {
      setMultiScenarioData(null);
      return;
    }

    setAclDataLoading(true);
    try {
      console.log('=== LOADING MULTI-SCENARIO DATA ===');
      console.log('Selected scenarios:', selectedAclScenarios);
      console.log('Baseline available:', scenarioMatrix.baseline);
      
      // Always include baseline + selected scenarios
      const scenariosToLoad = scenarioMatrix.baseline 
        ? [scenarioMatrix.baseline.session_id, ...selectedAclScenarios]
        : selectedAclScenarios;
      
      console.log('Scenarios to load (with baseline):', scenariosToLoad);
      
      // Fetch quota utilization for each scenario
      const promises = scenariosToLoad.map((sessionId, index) => {
        const params = {
          session_id: sessionId,
          simulation_year: selectedYear !== 'total' ? parseInt(selectedYear) : null
        };
        console.log(`Making API call ${index} for session ${sessionId}:`, params);
        return getFleetQuotaUtilization(params);
      });

      console.log('Making', promises.length, 'API calls...');
      const responses = await Promise.all(promises);
      console.log('Received', responses.length, 'responses');
      
      // Process and combine the data
      const combinedData = combineScenarioData(responses, selectedAclScenarios);
      setMultiScenarioData(combinedData);
      
      console.log('✓ Multi-scenario data loaded successfully. Data points:', combinedData?.length || 0);
      
    } catch (err) {
      console.error('Error loading multi-scenario quota data:', err);
      setMultiScenarioData(null);
    } finally {
      setAclDataLoading(false);
    }
  }, [selectedAclScenarios, selectedYear, scenarioMatrix, getFleetQuotaUtilization, combineScenarioData]);

  // Load multi-scenario data when scenarios change
  useEffect(() => {
    if (selectedAclScenarios.length > 0) {
      loadMultiScenarioQuotaData();
    } else {
      setMultiScenarioData(null);
    }
  }, [selectedAclScenarios, selectedYear, loadMultiScenarioQuotaData]);

  if (parentLoading || !aclAnalyses || aclAnalyses.length === 0) {
    return (
      <div className="acl-scenario-component">
        <h4>ACL Sensitivity Analysis</h4>
        <p style={{ color: '#666', fontStyle: 'italic' }}>
          {parentLoading ? 'Loading ACL scenarios...' : 'No ACL sensitivity scenarios available'}
        </p>
      </div>
    );
  }

  return (
    <div className="acl-scenario-component">
      {/* Scenario Selection Table */}
      <div className="scenario-selector">
        <div className="scenario-header">
          <h4>Compare ACL Sensitivity Scenarios</h4>
          <div className="scenario-controls">
            <button 
              onClick={handleSelectAll} 
              className="btn-secondary small"
              type="button"
            >
              Select All
            </button>
            <button 
              onClick={handleClearAll} 
              className="btn-secondary small"
              type="button"
            >
              Clear All
            </button>
          </div>
        </div>
        
        <div className="scenario-table">
          <table className="scenario-matrix-table">
            <thead>
              <tr>
                <th>Species</th>
                {scenarioMatrix.increments.map(increment => (
                  <th key={increment}>{increment}%</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scenarioMatrix.species.map(species => (
                <tr key={species}>
                  <td className="species-name">{species}</td>
                  {scenarioMatrix.increments.map(increment => {
                    const scenario = scenarioMatrix.matrix[species][increment];
                    const isSelected = scenario && selectedAclScenarios.includes(scenario.session_id);
                    const isDisabled = !scenario;
                    
                    return (
                      <td key={increment} className="scenario-cell">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => scenario && handleScenarioToggle(scenario.session_id)}
                          className={isDisabled ? 'disabled-checkbox' : ''}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="scenario-summary">
          <p>
            {selectedAclScenarios.length} scenarios selected 
            {scenarioMatrix.baseline && selectedAclScenarios.length > 0 && ' (+ baseline always included)'}
          </p>
        </div>
      </div>

      {/* Fleet Quota Utilization Chart */}
      <div className="quota-utilization-chart">
        <h3>Fleet Quota Utilization - Multi-Scenario Comparison</h3>
        {multiScenarioData && multiScenarioData.length > 0 ? (
          <div style={{ height: '500px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={multiScenarioData}
                margin={{ top: 20, right: 30, left: 40, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="species_name" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                  fontSize={11}
                />
                <YAxis label={{ value: 'Utilization %', angle: -90, position: 'insideLeft' }} />
                <Tooltip 
                  formatter={(value, name) => {
                    // Find the label for this session ID
                    const legendItem = getLegendLabels.find(item => item.sessionId === name);
                    const label = legendItem ? legendItem.label : `Scenario`;
                    return [`${value.toFixed(1)}%`, label];
                  }}
                  labelFormatter={(label) => `Species: ${label}`}
                />
                <Legend 
                  formatter={(value) => {
                    // Find the label for this session ID
                    const legendItem = getLegendLabels.find(item => item.sessionId === value);
                    return legendItem ? legendItem.label : value;
                  }}
                />
                
                {/* Generate a Bar for each scenario (baseline + selected) */}
                {getLegendLabels.map((legendItem, index) => (
                  <Bar 
                    key={legendItem.sessionId}
                    dataKey={legendItem.sessionId}
                    fill={legendItem.color}
                    name={legendItem.sessionId} // This gets transformed by Legend formatter
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            {selectedAclScenarios.length === 0 
              ? 'Select ACL scenarios above to compare quota utilization'
              : aclDataLoading 
                ? 'Loading multi-scenario quota utilization data...'
                : 'No quota utilization data available for selected scenarios'
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default AclScenarioComponent;